<script setup lang="ts" generic="T">
import type {
	AcceptableValue,
	ClickedHighlightedItem,
	DefaultFilterSelectProps
} from '.';

import { cn } from '@/utils';
import { getBoldMatchCaracteres } from '~~/utils';

import {
	Command,
	CommandInput,
	CommandItem,
	CommandList
} from '~/components/ui/command';

import { separatorHighlightedItemsVariant } from '.';
import messages from './DefaultFilterSelect.locale.json';
import {
	defaultFilterSelectBadgeIconVariants,
	defaultFilterSelectBadgeVariants,
	defaultFilterSelectCancelIconVariants,
	defaultFilterSelectCommandVariants,
	defaultFilterSelectHighlightBadgeIconVariants,
	defaultFilterSelectHighlightBadgeVariants,
	defaultFilterSelectHighlightTitleVariants,
	defaultFilterSelectIconVariants,
	defaultFilterSelectInputVariants,
	defaultFilterSelectInputWrapperVariants,
	defaultFilterSelectItemVariants,
	defaultFilterSelectListVariants,
	defaultFilterSelectSkeletonPrimaryVariants,
	defaultFilterSelectSkeletonSecondaryVariants,
	defaultFilterSelectTitleVariants
} from './variants';

interface ItemWithUniqueKey {
	uniqueKey?: string;
}

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<DefaultFilterSelectProps<T>>(), {
	title: '',
	items: () => [],
	highlightedItems: () => [],
	getItemLabel: (item: T) => `${item}`,
	filterFunction: (item: T[], term: string) => item,
	objectValue: false,
	valueProp: '',
	hideWhenDetached: true,
	isLoading: false,
	variant: 'primary'
});

const modelValue = defineModel<T[]>({ required: true, default: () => [] });
const searchTerm = shallowRef<string>('');
const isDropdownOpened = shallowRef<boolean>(false);
const commandEl = ref<HTMLElement | null>(null);

watch(modelValue, () => (isDropdownOpened.value = false));

const inputPlaceholder = computed<string>(() =>
	t('placeholder', { title: props.title.toLowerCase() })
);

const filteredHighlightedItems = computed(() => {
	return props.highlightedItems.filter(item => {
		const itemValue = getItemValue(item) as T;

		// Check if the item is already selected using our centralized comparison
		const isAlreadySelected = modelValue.value.some(model =>
			itemsAreEqual(itemValue, model)
		);

		return !isAlreadySelected;
	});
});

const hasHilightedItems = computed<boolean>(
	() => !!filteredHighlightedItems.value.length
);

function clear() {
	searchTerm.value = '';
}

function getItemValue(item: T): AcceptableValue {
	if (typeof item === 'object' && props.objectValue && props.valueProp) {
		return (item?.[props.valueProp as keyof typeof item] ||
			item) as AcceptableValue;
	}

	return item as AcceptableValue;
}

const emit = defineEmits<{
	(event: 'click-highlight-item', item: ClickedHighlightedItem<T>): void;
}>();

function itemsAreEqual(itemA: T, itemB: T): boolean {
	const keyA = (itemA as ItemWithUniqueKey)?.uniqueKey;
	if (keyA !== undefined) {
		const keyB = (itemB as ItemWithUniqueKey)?.uniqueKey;
		return keyA === keyB;
	}
	return itemA === itemB;
}

function updateModel(value: T) {
	const isSelected = modelValue.value.some(item => itemsAreEqual(item, value));
	if (isSelected) {
		modelValue.value = modelValue.value.filter(v => !itemsAreEqual(v, value));
	} else {
		modelValue.value = [...modelValue.value, value];
	}
}

function updateModelHighlightItem(value: T, index: number) {
	updateModel(value);
	emit('click-highlight-item', { item: value, index });
}

function isItemSelected(item: T): boolean {
	return modelValue.value.some(selectedItem =>
		itemsAreEqual(selectedItem, item)
	);
}

onClickOutside(commandEl, () => {
	isDropdownOpened.value = false;
});

defineExpose({ searchTerm });
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<div class="w-full space-y-5">
		<p :class="defaultFilterSelectTitleVariants({ variant })">
			{{ title }}
		</p>

		<div ref="commandEl" class="relative size-auto h-[50px]">
			<Command
				v-model:model-value="modelValue"
				v-model:open="isDropdownOpened"
				:filter-function="filterFunction as any"
				class="absolute z-50"
				:class="
					cn(defaultFilterSelectCommandVariants({ variant }), props.class)
				"
				multiple
			>
				<CommandInput
					v-model.trim="searchTerm"
					:placeholder="inputPlaceholder"
					:wrapper-classes="
						defaultFilterSelectInputWrapperVariants({ variant })
					"
					:class="defaultFilterSelectInputVariants({ variant })"
					@focus="isDropdownOpened = true"
				>
					<template #prefix>
						<div class="grid h-full w-min place-items-center pl-3">
							<IconMaterialSymbolsLightSearch
								:class="defaultFilterSelectIconVariants({ variant })"
							/>
						</div>
					</template>
					<template #suffix>
						<div
							v-show="searchTerm"
							class="mr-3 flex h-full w-7 items-center justify-end"
						>
							<IconMaterialSymbolsLightCancel
								:class="defaultFilterSelectCancelIconVariants({ variant })"
								@click="clear"
							/>
						</div>
					</template>
				</CommandInput>

				<CommandList
					class="p-0"
					:class="[
						(items.length || isLoading) &&
							defaultFilterSelectListVariants({ variant }),
						(items.length || isLoading) && 'border'
					]"
				>
					<!-- Loading State -->
					<div v-if="isLoading" class="flex flex-col gap-2 p-4">
						<div
							v-for="i in 3"
							:key="`loading-skeleton-${i}`"
							class="flex animate-pulse flex-col gap-1"
						>
							<div
								:class="defaultFilterSelectSkeletonPrimaryVariants({ variant })"
							/>
							<div
								:class="
									defaultFilterSelectSkeletonSecondaryVariants({ variant })
								"
							/>
						</div>
					</div>

					<!-- Items List -->
					<template v-else>
						<CommandItem
							v-for="item in items"
							:key="`default-filter-select-${title}-${(item as any)?.uniqueKey || getItemLabel(item)}`"
							:value="getItemValue(item)"
							:class="
								defaultFilterSelectItemVariants({
									variant,
									isActive: isItemSelected(item)
								})
							"
						>
							<slot name="item" :item="item" :search-term="searchTerm">
								<p
									v-html="
										getBoldMatchCaracteres(getItemLabel(item), searchTerm)
									"
								/>
							</slot>
						</CommandItem>
					</template>
				</CommandList>
			</Command>
		</div>

		<div v-show="modelValue.length" class="flex flex-wrap gap-2 pt-2">
			<Badge
				v-for="item in modelValue"
				:key="`default-filter-select-badge-${(item as any)?.uniqueKey || getItemLabel(item)}`"
				variant="primary"
				:class="defaultFilterSelectBadgeVariants({ variant })"
				@click="updateModel(item)"
			>
				<slot name="selected-item-badge" :item="item" :search-term="searchTerm">
					{{ getItemLabel(item) }}
				</slot>
				<IconMaterialSymbolsLightClose
					:class="defaultFilterSelectBadgeIconVariants({ variant })"
				/>
			</Badge>
		</div>

		<Separator
			v-if="hasHilightedItems && modelValue.length"
			:class="
				cn(separatorHighlightedItemsVariant({ variant: separatorVariant }))
			"
		/>

		<div v-if="hasHilightedItems">
			<p :class="defaultFilterSelectHighlightTitleVariants({ variant })">
				{{ t('highlightTitle', { title }) }}
			</p>
			<div class="flex flex-wrap gap-2 pt-2">
				<Badge
					v-for="(item, index) in filteredHighlightedItems"
					:key="`default-filter-select-highlighted-badge-${(item as ItemWithUniqueKey)?.uniqueKey || getItemLabel(item)}`"
					variant="tertiary"
					:class="defaultFilterSelectHighlightBadgeVariants({ variant })"
					@click="updateModelHighlightItem(item, index)"
				>
					<IconMaterialSymbolsLightAdd
						:class="defaultFilterSelectHighlightBadgeIconVariants({ variant })"
					/>
					<slot
						name="highlighted-item-badge"
						:item="item"
						:search-term="searchTerm"
					>
						{{ getItemLabel(item) }}
					</slot>
				</Badge>
			</div>
		</div>
	</div>
</template>
